/**
 * FairwayBid – Beschaffungsfenster einer Golfanlage
 *
 * Golfanlagen kaufen nicht gleichmäßig übers Jahr. Die einen entscheiden im
 * Herbst über das Budget fürs Folgejahr, die anderen kurz vor Saisonstart im
 * Frühjahr, manche laufend. Bis jetzt galten für alle dieselben zwei Fenster
 * aus seasons.js – und damit bekam eine Anlage, die nur im Frühjahr
 * beschafft, im September eine Erinnerung, die sie nichts anging.
 *
 * Sie sagt es uns jetzt selbst, einmal, im Konto. Danach richtet sich die
 * Startseite danach.
 *
 * Nicht bei der Registrierung abgefragt: "Registrierung in zwei Minuten" ist
 * ein Versprechen, und diese Angabe kann jederzeit nachgetragen werden. Die
 * Startseite fragt danach, solange sie fehlt.
 */
import { PERIODS } from './seasons';

export const GOLFANLAGE_USER_TYPE = 'customer';
export const FENSTER_KEY = 'beschaffungsfenster';

/** Die Postleitzahl der Anlage – Grundlage der Wetteraussicht. */
export const PLZ_KEY = 'plz';

/** Die Antwortmöglichkeiten und die Perioden, für die sie stehen. */
export const FENSTER_OPTIONEN = [
  {
    option: 'herbst',
    label: 'Im Herbst für das Folgejahr (September bis Januar)',
    perioden: ['haupt'],
  },
  {
    option: 'fruehjahr',
    label: 'Im Frühjahr vor Saisonstart (März bis April)',
    perioden: ['fruehjahr'],
  },
  { option: 'beides', label: 'In beiden Zeiträumen', perioden: ['haupt', 'fruehjahr'] },
  { option: 'laufend', label: 'Laufend über das Jahr verteilt', perioden: [] },
];

/**
 * Das Nutzerfeld. "private", weil es niemanden außer der Anlage und uns
 * etwas angeht – es steuert nur, woran wir erinnern.
 */
export const FWB_CLUB_FIELDS = [
  {
    key: FENSTER_KEY,
    scope: 'private',
    schemaType: 'enum',
    enumOptions: FENSTER_OPTIONEN.map(({ option, label }) => ({ option, label })),
    helpText:
      'Danach richtet sich, wann wir Sie auf der Startseite an anstehende Ausschreibungen erinnern.',
    showConfig: { label: 'Beschaffungszeitraum', displayInProfile: false },
    saveConfig: {
      label: 'Wann beschaffen Sie?',
      placeholderMessage: 'Bitte wählen',
      isRequired: false,
      displayInSignUp: false,
    },
    userTypeConfig: { limitToUserTypeIds: true, userTypeIds: [GOLFANLAGE_USER_TYPE] },
  },
  /**
   * Die Postleitzahl.
   *
   * Sie steuert zwei Dinge, und beide sind es wert, einmal danach zu
   * fragen: die Wetteraussicht auf der Startseite und - später - welche
   * Anbieter überhaupt in Ihre Ecke liefern.
   *
   * "private", weil eine Adresse niemanden etwas angeht, solange kein
   * Geschäft zustande kommt. Die Wetterabfrage schickt daraus nur die
   * Koordinaten einer Leitregion nach außen, nie die Postleitzahl selbst.
   *
   * Freiwillig: Wer sie nicht angibt, bekommt keine Wetterkachel - das ist
   * ein fairer Tausch und kein Grund, eine Registrierung zu blockieren.
   */
  {
    key: PLZ_KEY,
    scope: 'private',
    schemaType: 'text',
    helpText:
      'Nur die Postleitzahl, keine Straße. Daraus zeigen wir Ihnen die Wetteraussicht für heute.',
    showConfig: { label: 'Postleitzahl', displayInProfile: false },
    saveConfig: {
      label: 'Postleitzahl Ihrer Anlage',
      placeholderMessage: 'z. B. 61200',
      isRequired: false,
      displayInSignUp: true,
    },
    userTypeConfig: { limitToUserTypeIds: true, userTypeIds: [GOLFANLAGE_USER_TYPE] },
  },
];

/** Die gewählte Option eines Nutzers, oder null. */
export const gewaehltesFenster = user =>
  FENSTER_OPTIONEN.find(o => o.option === user?.attributes?.profile?.privateData?.[FENSTER_KEY]) ||
  null;

/**
 * Welche Perioden gelten für diesen Nutzer?
 *
 * null heißt: nichts angegeben – dann gelten alle Fenster, wie bisher. Ein
 * leeres Array heißt: "laufend", also gar keins.
 */
export const periodenFuer = user => gewaehltesFenster(user)?.perioden ?? null;

/** Prüfsumme für Entwickler: Jede Option zeigt auf Perioden, die es gibt. */
export const unbekanntePerioden = () => {
  const bekannt = PERIODS.map(p => p.key);
  return FENSTER_OPTIONEN.flatMap(o => o.perioden).filter(k => !bekannt.includes(k));
};

/** Hängt das Feld hinten an – die Console gewinnt, falls es dort schon steht. */
export const mitClubFeldern = (hostedUserFields = []) => {
  const vorhanden = new Set(hostedUserFields.map(f => f?.key));
  return [...hostedUserFields, ...FWB_CLUB_FIELDS.filter(f => !vorhanden.has(f.key))];
};
